import React, { useState } from 'react';
import { Mail, Check, Loader2, Gift } from 'lucide-react';
import { API_BASE } from '../utils/apiBase';
import { useEmailCheck } from '../utils/emailCheck';
import { EmailCheckHint } from './EmailCheckHint';

/**
 * DSGVO-konforme Newsletter-Anmeldung mit Double-Opt-In.
 * Lead-Magnet: 5 EUR Rabatt ab 40 EUR Bestellwert (ZOZODEAL2025).
 * Honeypot-Field gegen Bots; echtes Feedback nach Versand.
 */
export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const emailCheck = useEmailCheck(email);
  const [firstName, setFirstName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }
    if (!agreed) {
      setError('Bitte bestätige den Hinweis zum Datenschutz.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim(),
          honeypot,
          source: 'homepage',
        }),
      });
      if (!res.ok) throw new Error('request_failed');
      setSubmitted(true);
    } catch (err) {
      setError('Etwas ist schiefgelaufen. Bitte versuche es später nochmal.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 md:p-10 text-center"
        data-testid="newsletter-signup-success"
      >
        <div className="w-14 h-14 rounded-full bg-emerald-500/20 mx-auto mb-4 flex items-center justify-center">
          <Check className="w-7 h-7 text-emerald-400" />
        </div>
        <h3 className="font-display text-2xl font-bold text-white mb-2">Fast geschafft!</h3>
        <p className="text-stone-300 text-sm leading-relaxed max-w-md mx-auto">
          Wir haben dir eine Bestätigungs-Mail an <strong className="text-white">{email}</strong> geschickt.
          Klick auf den Link in der Mail und du bekommst deinen <strong className="text-[#FCD34D]">5 EUR Willkommensrabatt</strong> freigeschaltet.
        </p>
        <p className="text-xs text-stone-500 mt-4">
          Keine Mail bekommen? Check den Spam-Ordner.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-10 backdrop-blur-sm"
      data-testid="newsletter-signup-form"
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#A81C1C] to-[#7A1414] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#A81C1C]/20">
          <Gift className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
            5&nbsp;EUR geschenkt
          </h3>
          <p className="text-stone-400 text-sm mt-1">
            Willkommensrabatt ab 40&nbsp;EUR Bestellwert &mdash; nur für Newsletter-Abonnenten.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-[1fr_1.3fr] gap-3 mb-4">
        <input
          type="text"
          placeholder="Vorname (optional)"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          maxLength={80}
          className="h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-stone-500 focus:border-[#A81C1C]/60 focus:outline-none focus:ring-2 focus:ring-[#A81C1C]/30 transition"
          data-testid="newsletter-firstname-input"
        />
        <input
          type="email"
          required
          placeholder="deine@email.de"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={160}
          autoComplete="email"
          className="h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-stone-500 focus:border-[#A81C1C]/60 focus:outline-none focus:ring-2 focus:ring-[#A81C1C]/30 transition"
          data-testid="newsletter-email-input"
        />
      </div>
      <EmailCheckHint check={emailCheck} onAccept={setEmail} />

      {/* Honeypot – hidden from humans */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        name="company_website"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />

      <label className="flex items-start gap-3 mb-4 cursor-pointer group">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-stone-600 bg-white/5 text-[#A81C1C] focus:ring-[#A81C1C]/40"
          data-testid="newsletter-consent-checkbox"
        />
        <span className="text-xs text-stone-400 leading-relaxed">
          Ja, ich möchte den ZOZO Burger Newsletter mit Angeboten und meinen 5 EUR Willkommensrabatt (ab 40 EUR) erhalten. Max. 1&ndash;2 Mails pro Monat, jederzeit mit einem Klick abbestellbar.{' '}
          <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition-colors">Datenschutz</a>.
        </span>
      </label>

      {error && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs" data-testid="newsletter-error">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !email || !agreed || emailCheck.status === 'invalid'}
        className="w-full h-12 rounded-xl bg-[#A81C1C] hover:bg-[#941818] disabled:bg-stone-700 disabled:text-stone-400 text-white font-bold text-sm transition-all shadow-lg shadow-[#A81C1C]/20 hover:shadow-[#A81C1C]/40 disabled:shadow-none flex items-center justify-center gap-2"
        data-testid="newsletter-submit-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet...
          </>
        ) : (
          <>
            <Mail className="w-4 h-4" /> Jetzt 5 EUR sichern
          </>
        )}
      </button>

      <p className="text-[11px] text-stone-500 mt-4 text-center">
        Nach dem Klick schicken wir dir eine Bestätigungs-Mail (Double-Opt-In). Erst danach bist du angemeldet.
      </p>
    </form>
  );
}
